export type BulkPricingField = 'retail_price' | 'install_cost' | 'cost_price';

export type BulkPricingMode = 'amount' | 'percent' | 'set';

export interface PricingAdjustmentFields {
  retail_price: number;
  install_cost: number;
  total_price: number;
  cost_price: number;
}

const roundCurrency = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100;

const clampCurrency = (value: number): number =>
  Math.max(0, roundCurrency(value));

const snapValueToStep = (
  nextValue: number,
  currentValue: number,
  step: number,
  mode: BulkPricingMode,
): number => {
  if (mode === 'set') {
    return clampCurrency(nextValue);
  }

  if (nextValue <= 0) {
    return 0;
  }

  const direction = nextValue - currentValue;

  if (direction > 0) {
    return clampCurrency(Math.ceil(nextValue / step) * step);
  }

  if (direction < 0) {
    return clampCurrency(Math.floor(nextValue / step) * step);
  }

  return clampCurrency(nextValue);
};

const getPsychPriceEnding = (currentRetailPrice: number): 95 | 99 => {
  const wholeDollarValue = Math.round(currentRetailPrice);
  return wholeDollarValue % 100 === 99 ? 99 : 95;
};

const snapRetailPriceToPsych = (
  nextRetailPrice: number,
  currentRetailPrice: number,
  mode: BulkPricingMode,
): number => {
  if (mode === 'set') {
    return clampCurrency(nextRetailPrice);
  }

  if (nextRetailPrice <= 0) {
    return 0;
  }

  const preferredEnding = getPsychPriceEnding(currentRetailPrice);
  const priceDirection = nextRetailPrice - currentRetailPrice;
  const hundredBase = Math.floor(nextRetailPrice / 100) * 100;
  const baseCandidate = hundredBase + preferredEnding;

  const lowerCandidate = baseCandidate <= nextRetailPrice
    ? baseCandidate
    : baseCandidate - 100;
  const upperCandidate = baseCandidate >= nextRetailPrice
    ? baseCandidate
    : baseCandidate + 100;

  if (priceDirection > 0) {
    return clampCurrency(upperCandidate);
  }

  if (priceDirection < 0) {
    return clampCurrency(lowerCandidate);
  }

  return clampCurrency(nextRetailPrice);
};

export const applyBulkPricingAdjustment = <T extends PricingAdjustmentFields>(
  row: T,
  field: BulkPricingField,
  mode: BulkPricingMode,
  amount: number,
): PricingAdjustmentFields => {
  const currentValue = row[field];
  const nextValue = mode === 'percent'
    ? currentValue * (1 + amount / 100)
    : mode === 'set'
      ? amount
      : currentValue + amount;

  const updatedRow: PricingAdjustmentFields = {
    retail_price: row.retail_price,
    install_cost: row.install_cost,
    total_price: row.total_price,
    cost_price: row.cost_price,
    [field]: field === 'retail_price'
      ? snapRetailPriceToPsych(nextValue, row.retail_price, mode)
      : field === 'install_cost'
        ? snapValueToStep(nextValue, row.install_cost, 100, mode)
      : clampCurrency(nextValue),
  };

  if (field === 'retail_price' || field === 'install_cost') {
    updatedRow.total_price = roundCurrency(updatedRow.retail_price + updatedRow.install_cost);
  }

  return updatedRow;
};